import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserX } from 'lucide-react';
import PageLayout from '@components/layout/PageLayout';
import ListPageHeader from '@components/common/ListPageHeader';
import Button from '@components/common/Button';
import Badge from '@components/common/Badge';
import { RZTable, DateCell } from '@components/common/table';
import ErrorState from '@components/common/ErrorState';
import ConfirmationModal from '@components/common/modals/ConfirmationModal';
import { useAuth } from '@contexts/AuthContext';
import { conflictService } from '@services';
import { ROLES } from '@app-types';
import type { Conflict, MissingRoleAssignment, MissingRoleField } from '@app-types/conflict';
import type { TableColumn } from '@app-types';
import { hasRole } from '@utils/permissionUtils';
import { MISSING_CONTRACT_ROLE_WORKLIST_KEY } from '@hooks/useMissingContractRoleCount';
import { formatInstantAsDate } from '@utils/dateUtils';
import { showToast } from '@utils/toast';
import { getApiErrorMessage } from '@utils/apiError';
import styles from './MissingContractRoleWorklistPage.module.css';

const PAGE_SIZE_OPTIONS = [25, 50, 100];

interface WorklistRow {
  conflict: Conflict;
  assignment: MissingRoleAssignment;
}

const columns: TableColumn[] = [
  { key: 'contact', label: 'Contact', defaultVisible: true },
  { key: 'contract', label: 'Contract', defaultVisible: true },
  { key: 'field', label: 'Field', defaultVisible: true },
  { key: 'detected', label: 'Detected', defaultVisible: true },
  { key: 'action', label: '', defaultVisible: true, alwaysVisible: true }
];

const fieldLabel = (field: MissingRoleField): string =>
  field === 'CO' ? 'Contract Owner' : 'Contract Manager';

const getAssignments = (conflict: Conflict): MissingRoleAssignment[] => {
  const raw = conflict.context.missing_role_assignments;
  return Array.isArray(raw) ? (raw as MissingRoleAssignment[]) : [];
};

const getContractNumber = (conflict: Conflict): string =>
  conflict.entity_info?.contract_number ?? String(conflict.context.contract_number ?? '-');

const MissingContractRoleWorklistPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  // "Role not needed" follows the conflict type's resolver role
  const canDismiss = hasRole(user, ROLES.ADMIN);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [pendingDismiss, setPendingDismiss] = useState<WorklistRow | null>(null);

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: [...MISSING_CONTRACT_ROLE_WORKLIST_KEY, currentPage, pageSize],
    queryFn: () =>
      conflictService.getWorklist({
        conflictTypeCode: 'MISSING_CONTRACT_ROLE',
        limit: pageSize,
        offset: (currentPage - 1) * pageSize
      })
  });

  // Prefix match refetches this page and the dashboard count tile
  const dismissMutation = useMutation({
    mutationFn: ({ conflict, assignment }: WorklistRow) =>
      conflictService.dismissMissingContractRole(conflict.id, assignment.contact_id, assignment.field),
    onSuccess: () => {
      showToast.success('Marked as Role not needed');
      setPendingDismiss(null);
      void queryClient.invalidateQueries({ queryKey: MISSING_CONTRACT_ROLE_WORKLIST_KEY });
    },
    onError: (err: unknown) => {
      showToast.error(getApiErrorMessage(err, 'Failed to mark Role not needed'));
    }
  });

  const rows: WorklistRow[] = (data?.conflicts ?? []).flatMap((conflict) =>
    getAssignments(conflict).map((assignment) => ({ conflict, assignment }))
  );
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const renderCell = (column: TableColumn, row: WorklistRow) => {
    switch (column.key) {
      case 'contact':
        return (
          <button
            type="button"
            className={styles.tableLink}
            onClick={(event) => {
              event.stopPropagation();
              navigate(`/users/${row.assignment.contact_id}`);
            }}
          >
            {row.assignment.contact_name}
          </button>
        );
      case 'contract':
        return (
          <div className={styles.contractCell}>
            <button
              type="button"
              className={styles.tableLink}
              onClick={(event) => {
                event.stopPropagation();
                navigate(`/contracts/${row.conflict.entity_id}`);
              }}
            >
              {getContractNumber(row.conflict)}
            </button>
            <div className={styles.contractName}>{row.conflict.entity_info?.contract_name ?? ''}</div>
          </div>
        );
      case 'field':
        return <Badge variant="default">{fieldLabel(row.assignment.field)}</Badge>;
      case 'detected':
        return <DateCell date={row.conflict.detected_at} formatDate={(d) => formatInstantAsDate(d, 'Unknown')} />;
      case 'action':
        if (!canDismiss) return null;
        return (
          <Button
            variant="secondary"
            size="sm"
            onClick={(event) => {
              event.stopPropagation();
              setPendingDismiss(row);
            }}
          >
            Role not needed
          </Button>
        );
      default:
        return '-';
    }
  };

  if (error) {
    return (
      <PageLayout>
        <ErrorState message="Failed to load missing Contract Role worklist." />
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Missing Contract Role">
      <ListPageHeader icon={<UserX size={24} />} title="Missing Contract Role" count={total} />

      <RZTable
        data={rows}
        columns={columns}
        getRowKey={(row) => `${row.conflict.id}-${row.assignment.contact_id}-${row.assignment.field}`}
        renderCell={renderCell}
        pagination={{
          currentPage,
          totalPages,
          totalItems: total,
          pageSize,
          onPageChange: setCurrentPage,
          onPageSizeChange: (size) => {
            setPageSize(size);
            setCurrentPage(1);
          },
          itemName: 'assignments',
          pageSizeOptions: PAGE_SIZE_OPTIONS
        }}
        state={{
          isLoading,
          isFetching,
          isEmpty: rows.length === 0,
          emptyTitle: 'No missing Contract Role assignments',
          emptyDescription: 'Every assigned Contract Owner and Contract Manager currently holds the Contract Role.',
          loadingMessage: 'Loading missing Contract Role worklist...'
        }}
      />

      <ConfirmationModal
        isOpen={Boolean(pendingDismiss)}
        onClose={() => setPendingDismiss(null)}
        onConfirm={() => {
          if (pendingDismiss) dismissMutation.mutate(pendingDismiss);
        }}
        title="Role not needed?"
        message={
          pendingDismiss
            ? `${pendingDismiss.assignment.contact_name} stays ${fieldLabel(pendingDismiss.assignment.field)} on ${getContractNumber(pendingDismiss.conflict)} without the Contract Role and is no longer pending there. They come back only if newly assigned again.`
            : ''
        }
        confirmLabel="Role not needed"
        confirmVariant="primary"
        isProcessing={dismissMutation.isPending}
        dataTestId="missing-contract-role-dismiss-modal"
      />
    </PageLayout>
  );
};

export default MissingContractRoleWorklistPage;
